/**
 * Typography token definitions for different product contexts.
 *
 * ProductContext determines typography scales — font sizes, line heights,
 * and font weights vary between B2B, B2C, Pay Station, and Presentation.
 *
 * Values are synced from the Figma "Theme / Typography" variable collection.
 * Each semantic token (e.g., heading/h1/font-size) aliases into the responsive
 * scale defined in responsive-typography.ts. The `scaleStep` field records
 * which scale step a token maps to, enabling CSS variable consumption on web.
 *
 * Line-height categories:
 *   - Headings and display use "display" line-heights (tight, ~1:1)
 *   - Body default/accent use "compact" line-heights (moderate)
 *   - Body paragraph uses "text" line-heights (generous, for reading)
 */

import {
  fontSize,
  lineHeightDisplay,
  lineHeightCompact,
  lineHeightText,
} from "./responsive-typography";

export type ProductContext = "b2c" | "b2b";

export interface TypographyVariant {
  fontSize: number;
  lineHeight: string;
  fontWeight: number;
  /** The responsive scale step this variant maps to (e.g., "650" for 56px) */
  scaleStep?: string;
  /** The line-height category: "display", "compact", or "text" */
  lineHeightCategory?: "display" | "compact" | "text";
}

export interface TypographyBodyVariant extends TypographyVariant {
  accent?: {
    fontWeight: number;
  };
  paragraph?: {
    lineHeight: string;
    spacing?: number;
    /** The line-height scale step for the paragraph variant */
    lineHeightScaleStep?: string;
  };
}

export interface TypographyTokens {
  heading: {
    h1: TypographyVariant;
    h2: TypographyVariant;
    h3: TypographyVariant;
    h4: TypographyVariant;
    h5: TypographyVariant;
  };
  basic: {
    display: TypographyVariant;
    "body-lg": TypographyBodyVariant;
    "body-md": TypographyBodyVariant;
    "body-sm": TypographyBodyVariant;
    "body-xs": TypographyBodyVariant;
    "body-xxs": TypographyBodyVariant;
  };
}

export const typographyTokens: Record<ProductContext, TypographyTokens> = {
  b2c: {
    heading: {
      h1: {
        fontSize: fontSize["450"].common,
        lineHeight: `${lineHeightDisplay["450"].common}px`,
        fontWeight: 700,
        scaleStep: "450",
        lineHeightCategory: "display",
      },
      h2: {
        fontSize: fontSize["350"].common,
        lineHeight: `${lineHeightDisplay["350"].common}px`,
        fontWeight: 700,
        scaleStep: "350",
        lineHeightCategory: "display",
      },
      h3: {
        fontSize: fontSize["300"].common,
        lineHeight: `${lineHeightDisplay["300"].common}px`,
        fontWeight: 700,
        scaleStep: "300",
        lineHeightCategory: "display",
      },
      h4: {
        fontSize: fontSize["250"].common,
        lineHeight: `${lineHeightDisplay["250"].common}px`,
        fontWeight: 700,
        scaleStep: "250",
        lineHeightCategory: "display",
      },
      h5: {
        fontSize: fontSize["200"].common,
        lineHeight: `${lineHeightDisplay["200"].common}px`,
        fontWeight: 700,
        scaleStep: "200",
        lineHeightCategory: "display",
      },
    },
    basic: {
      display: {
        fontSize: fontSize["550"].common,
        lineHeight: `${lineHeightDisplay["550"].common}px`,
        fontWeight: 800,
        scaleStep: "550",
        lineHeightCategory: "display",
      },
      "body-lg": {
        fontSize: fontSize["175"].common,
        lineHeight: `${lineHeightCompact["175"].common}px`,
        fontWeight: 400,
        scaleStep: "175",
        lineHeightCategory: "compact",
        accent: { fontWeight: 500 },
        paragraph: {
          lineHeight: `${lineHeightText["175"].common}px`,
          spacing: 20,
          lineHeightScaleStep: "175",
        },
      },
      "body-md": {
        fontSize: fontSize["150"].common,
        lineHeight: `${lineHeightCompact["150"].common}px`,
        fontWeight: 400,
        scaleStep: "150",
        lineHeightCategory: "compact",
        accent: { fontWeight: 500 },
        paragraph: {
          lineHeight: `${lineHeightText["150"].common}px`,
          spacing: 16,
          lineHeightScaleStep: "150",
        },
      },
      "body-sm": {
        fontSize: fontSize["125"].common,
        lineHeight: `${lineHeightCompact["125"].common}px`,
        fontWeight: 400,
        scaleStep: "125",
        lineHeightCategory: "compact",
        accent: { fontWeight: 500 },
        paragraph: {
          lineHeight: `${lineHeightText["125"].common}px`,
          spacing: 12,
          lineHeightScaleStep: "125",
        },
      },
      "body-xs": {
        fontSize: fontSize["100"].common,
        lineHeight: `${lineHeightCompact["100"].common}px`,
        fontWeight: 400,
        scaleStep: "100",
        lineHeightCategory: "compact",
        accent: { fontWeight: 500 },
        paragraph: {
          lineHeight: `${lineHeightText["100"].common}px`,
          spacing: 8,
          lineHeightScaleStep: "100",
        },
      },
      "body-xxs": {
        fontSize: fontSize["75"].common,
        lineHeight: `${lineHeightCompact["75"].common}px`,
        fontWeight: 400,
        scaleStep: "75",
        lineHeightCategory: "compact",
        accent: { fontWeight: 500 },
        paragraph: {
          lineHeight: `${lineHeightText["75"].common}px`,
          spacing: 6,
          lineHeightScaleStep: "75",
        },
      },
    },
  },
  b2b: {
    heading: {
      h1: {
        fontSize: fontSize["650"].common,
        lineHeight: `${lineHeightDisplay["650"].common}px`,
        fontWeight: 400,
        scaleStep: "650",
        lineHeightCategory: "display",
      },
      h2: {
        fontSize: fontSize["550"].common,
        lineHeight: `${lineHeightDisplay["550"].common}px`,
        fontWeight: 400,
        scaleStep: "550",
        lineHeightCategory: "display",
      },
      h3: {
        fontSize: fontSize["350"].common,
        lineHeight: `${lineHeightDisplay["350"].common}px`,
        fontWeight: 400,
        scaleStep: "350",
        lineHeightCategory: "display",
      },
      h4: {
        fontSize: fontSize["250"].common,
        lineHeight: `${lineHeightDisplay["250"].common}px`,
        fontWeight: 400,
        scaleStep: "250",
        lineHeightCategory: "display",
      },
      h5: {
        fontSize: fontSize["200"].common,
        lineHeight: `${lineHeightDisplay["200"].common}px`,
        fontWeight: 600,
        scaleStep: "200",
        lineHeightCategory: "display",
      },
    },
    basic: {
      display: {
        fontSize: fontSize["750"].common,
        lineHeight: `${lineHeightDisplay["750"].common}px`,
        fontWeight: 400,
        scaleStep: "750",
        lineHeightCategory: "display",
      },
      "body-lg": {
        fontSize: fontSize["175"].common,
        lineHeight: `${lineHeightCompact["175"].common}px`,
        fontWeight: 400,
        scaleStep: "175",
        lineHeightCategory: "compact",
        accent: { fontWeight: 500 },
        paragraph: {
          lineHeight: `${lineHeightText["175"].common}px`,
          spacing: 20,
          lineHeightScaleStep: "175",
        },
      },
      "body-md": {
        fontSize: fontSize["150"].common,
        lineHeight: `${lineHeightCompact["150"].common}px`,
        fontWeight: 400,
        scaleStep: "150",
        lineHeightCategory: "compact",
        accent: { fontWeight: 500 },
        paragraph: {
          lineHeight: `${lineHeightText["150"].common}px`,
          spacing: 16,
          lineHeightScaleStep: "150",
        },
      },
      "body-sm": {
        fontSize: fontSize["125"].common,
        lineHeight: `${lineHeightCompact["125"].common}px`,
        fontWeight: 400,
        scaleStep: "125",
        lineHeightCategory: "compact",
        accent: { fontWeight: 500 },
        paragraph: {
          lineHeight: `${lineHeightText["125"].common}px`,
          spacing: 12,
          lineHeightScaleStep: "125",
        },
      },
      "body-xs": {
        fontSize: fontSize["100"].common,
        lineHeight: `${lineHeightCompact["100"].common}px`,
        fontWeight: 400,
        scaleStep: "100",
        lineHeightCategory: "compact",
        accent: { fontWeight: 500 },
        paragraph: {
          lineHeight: `${lineHeightText["100"].common}px`,
          spacing: 8,
          lineHeightScaleStep: "100",
        },
      },
      "body-xxs": {
        fontSize: fontSize["75"].common,
        lineHeight: `${lineHeightCompact["75"].common}px`,
        fontWeight: 400,
        scaleStep: "75",
        lineHeightCategory: "compact",
        accent: { fontWeight: 500 },
        paragraph: {
          lineHeight: `${lineHeightText["75"].common}px`,
          spacing: 6,
          lineHeightScaleStep: "75",
        },
      },
    },
  },
};

export const defaultProductContext: ProductContext = "b2b";

export const getTypographyTokens = (
  productContext: ProductContext = defaultProductContext
): TypographyTokens => {
  return (
    typographyTokens[productContext] || typographyTokens[defaultProductContext]
  );
};

export const getTypographyVariant = (
  productContext: ProductContext,
  variant: string
): TypographyVariant | TypographyBodyVariant | undefined => {
  const tokens = getTypographyTokens(productContext);

  if (variant in tokens.heading) {
    return tokens.heading[variant as keyof typeof tokens.heading];
  }

  if (variant === "display") {
    return tokens.basic.display;
  }

  const bodyVariantMap: Record<string, keyof typeof tokens.basic> = {
    bodyLg: "body-lg",
    bodyMd: "body-md",
    bodySm: "body-sm",
    bodyXs: "body-xs",
    bodyXxs: "body-xxs",
  };

  const bodyKey =
    bodyVariantMap[variant.replace("Accent", "").replace("Paragraph", "")];
  if (bodyKey && bodyKey in tokens.basic) {
    const baseVariant = tokens.basic[
      bodyKey as keyof typeof tokens.basic
    ] as TypographyBodyVariant;

    if (variant.includes("Accent") && baseVariant.accent) {
      return {
        ...baseVariant,
        fontWeight: baseVariant.accent.fontWeight,
      };
    }

    if (variant.includes("Paragraph") && baseVariant.paragraph) {
      return {
        ...baseVariant,
        lineHeight: baseVariant.paragraph.lineHeight,
      };
    }

    return baseVariant;
  }

  return undefined;
};
